import React, { useEffect, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MedalIcon, UsersIcon, ShieldIcon, BuildingIcon, PackageIcon, CalendarIcon, MailIcon, TargetIcon, TrendingUpIcon, HeartIcon, MessageCircleIcon, SparklesIcon, CoffeeIcon, SmileIcon, CreditCardIcon, ZapIcon, UnlockIcon, AwardIcon, ExternalLinkIcon, MapPinIcon, CalendarDaysIcon, ClockIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { SectionHeader } from '../components/SectionHeader';
import { ValuesSection } from '../components/ValuesSection';
export function BadmintonPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white pt-20" data-id="element-383">
      {/* Hero Section */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px] overflow-hidden bg-brand-blue" data-id="element-384">
        <img src="https://cdn.magicpatterns.com/uploads/pTRbE2fvqTGzPDd7dvdT5h/Group1.webp" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-385" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-386"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-387">
          <div className="mb-8" data-id="element-388">
            <nav className="flex text-sm font-medium text-white/70 mb-8" data-id="element-389">
              <Link to="/" className="hover:text-white transition-colors" data-id="element-390">
                Home
              </Link>
              <span className="mx-2" data-id="element-391">/</span>
              <span className="cursor-default" data-id="element-392">Activities</span>
              <span className="mx-2" data-id="element-393">/</span>
              <span className="text-white" data-id="element-394">Badminton</span>
            </nav>
          </div>

          <div className="max-w-3xl" data-id="element-395">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-396">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight" data-id="element-397">
                From First Racket to Competition Ready
              </h1>
              <p className="text-xl text-white/90 mb-10 max-w-2xl leading-relaxed" data-id="element-398">
                Olympian-led badminton coaching developing technique, agility,
                and game sense for young athletes of all abilities.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-399">
                <Button variant="primary" size="lg" href="/book" data-id="element-400">
                  Book Now
                </Button>
                <Button variant="secondary" size="lg" href="/#contact" className="border-white text-white hover:bg-white/20" data-id="element-401">
                  Contact Us
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-402">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-403">
          <div data-id="element-404">
            <SectionHeader eyebrow="OVERVIEW" title="About Our Badminton Sessions" align="left" data-id="element-405" />
            <div className="space-y-6 text-lg text-navy-600" data-id="element-406">
              <p className="leading-relaxed" data-id="element-407">
                Our badminton programme is led by our team of Olympian coaches —
                Donna Kellogg MBE (Triple Olympian, 2000, 2004 & 2008), Natalie
                Barton (2004 Olympian, world top 5) and Sarah Chapman (2000
                Olympian, European medallist). Together they bring decades of
                elite experience to every session.
              </p>
              <p className="leading-relaxed" data-id="element-408">
                Whether your child is picking up a racket for the first time or
                looking to compete at county level, our structured sessions
                develop technique, agility, and game sense in a fun, supportive
                environment. We focus on personal improvement and celebrate
                small victories every week.
              </p>
              <p className="leading-relaxed" data-id="element-409">
                All necessary equipment, including age-appropriate rackets and
                shuttles, is provided. Children just need comfortable sports
                clothing and non-marking indoor trainers.
              </p>
            </div>
          </div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-navy-50 rounded-2xl p-8 md:p-12 border border-navy-100 relative overflow-hidden" data-id="element-410">
            <div className="absolute top-0 left-0 w-2 h-full bg-brand-blue" data-id="element-411"></div>
            <h3 className="text-2xl font-bold text-navy-900 mb-8" data-id="element-412">
              Programme Highlights
            </h3>
            <ul className="space-y-6" data-id="element-413">
              <li className="flex items-start" data-id="element-414">
                <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-gold-500" data-id="element-415">
                  <MedalIcon className="w-5 h-5" data-id="element-416" />
                </div>
                <div data-id="element-417">
                  <h4 className="font-bold text-navy-900" data-id="element-418">
                    Led by Our Olympian Coaches
                  </h4>
                  <p className="text-navy-600 text-sm mt-1" data-id="element-419">
                    Donna Kellogg MBE, Natalie Barton and Sarah Chapman bring
                    elite experience to every session.
                  </p>
                </div>
              </li>
              <li className="flex items-start" data-id="element-420">
                <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-gold-500" data-id="element-421">
                  <UsersIcon className="w-5 h-5" data-id="element-422" />
                </div>
                <div data-id="element-423">
                  <h4 className="font-bold text-navy-900" data-id="element-424">Small Group Sizes</h4>
                  <p className="text-navy-600 text-sm mt-1" data-id="element-425">
                    High coach-to-player ratio ensures individual attention for
                    every child.
                  </p>
                </div>
              </li>
              <li className="flex items-start" data-id="element-426">
                <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-gold-500" data-id="element-427">
                  <ShieldIcon className="w-5 h-5" data-id="element-428" />
                </div>
                <div data-id="element-429">
                  <h4 className="font-bold text-navy-900" data-id="element-430">Safe & Inclusive</h4>
                  <p className="text-navy-600 text-sm mt-1" data-id="element-431">
                    A welcoming environment for all abilities and backgrounds.
                  </p>
                </div>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Badminton in Schools */}
      <section className="py-16 md:py-20 bg-navy-50 border-t border-gray-100" data-id="element-432">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-433">
          <div className="text-center mb-12" data-id="element-434">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-red uppercase mb-4" data-id="element-435">
              Badminton in schools
            </h2>
            <h3 className="text-xl md:text-2xl font-bold text-brand-blue uppercase" data-id="element-436">
              INSPIRE - ENCOURAGE - ACHIEVE
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-437">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-438">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-439">
                <BuildingIcon className="w-7 h-7" data-id="element-440" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-441">
                School Partnerships
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-442">
                Bright Stars Sports coaches currently support a number of
                primary schools and senior schools across Derbyshire and we are
                very interested to grow the number of schools we work with.
              </p>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-443">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-444">
                <PackageIcon className="w-7 h-7" data-id="element-445" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-446">
                Equipment Provided
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-447">
                We have experience in a variety of spaces and have all the
                equipment required for delivery.
              </p>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-448">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-449">
                <CalendarIcon className="w-7 h-7" data-id="element-450" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-451">
                Events & Clubs
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-452">
                Our head coaches have attended school festivals, open days,
                provided taster days and deliver weekly breakfast and after
                school clubs to provide opportunities for children to try
                badminton.
              </p>
            </motion.div>

            {/* Card 4 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-453">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-454">
                <MailIcon className="w-7 h-7" data-id="element-455" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-456">
                Get in Touch
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-457">
                If you would like further information and discuss the
                possibility of introducing your children to badminton in your
                environment, please email:{' '}
                <a href="mailto:derby@brightstarsports.com" className="text-brand-blue hover:underline font-medium" data-id="element-458">
                  derby@brightstarsports.com
                </a>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Badminton Classes */}
      <section className="py-16 md:py-20 bg-white border-t border-gray-100" data-id="element-459">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-460">
          <div className="text-center mb-12" data-id="element-461">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-red uppercase mb-4" data-id="element-462">
              Badminton classes
            </h2>
            <h3 className="text-xl md:text-2xl font-bold text-brand-blue uppercase" data-id="element-463">
              Age Groups 7 - 18 Years
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-464">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-465">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-466">
                <TargetIcon className="w-7 h-7" data-id="element-467" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-468">
                Skills & Technique
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-469">
                Age appropriate badminton classes that encourage budding players
                to learn new skills, techniques and tactics. Players will be
                introduced to grips technique, the shots needed for the game,
                scoring and play fun and competitive games.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-470">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-471">
                <TrendingUpIcon className="w-7 h-7" data-id="element-472" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-473">
                Personal Development
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-474">
                Provides players with opportunities to develop their discipline,
                motivation and confidence through structured coaching and
                progressive challenges.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-475">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-476">
                <HeartIcon className="w-7 h-7" data-id="element-477" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-478">
                Teamwork & Fun
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-479">
                Warm ups and games are great for building teamwork, learning to
                cooperate, meet new friends and have fun in a supportive
                environment.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-480">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-481">
                <MessageCircleIcon className="w-7 h-7" data-id="element-482" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-483">
                All Ages Welcome
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-484">
                The age is a guideline and if children have played other racket
                sports before or are capable of striking the shuttle at an
                earlier age, please get in touch.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ladies Classes */}
      <section className="py-16 md:py-20 bg-navy-50 border-t border-gray-100" data-id="element-485">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-486">
          <div className="text-center mb-12" data-id="element-487">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-red uppercase" data-id="element-488">
              Ladies Classes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-489">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-490">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-491">
                <SparklesIcon className="w-7 h-7" data-id="element-492" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-493">
                Social & Beginner Friendly
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-494">
                The sessions are aimed at social and beginner players wanting to
                learn new skills, socialise with other like-minded ladies and
                keep active on a Thursday morning.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-495">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-496">
                <CoffeeIcon className="w-7 h-7" data-id="element-497" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-498">
                Welcoming Community
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-499">
                It is a friendly group with a wide range of ages and everyone
                would be made to feel very welcome. We stay on afterwards for a
                debrief and cup of tea for those that have the time.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-500">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-501">
                <SmileIcon className="w-7 h-7" data-id="element-502" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-503">
                Fun Guaranteed
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-504">
                The sessions are always good fun and guaranteed weekly laughs
                with these lovely ladies. If you are thinking about it, come and
                give it a go!
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-505">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-506">
                <CreditCardIcon className="w-7 h-7" data-id="element-507" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-508">
                Flexible Booking
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-509">
                It is a Pay As You Go session so this provides the flexibility
                you might require with work and other areas of your life.
              </p>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5,
          duration: 0.5
        }} className="rounded-2xl bg-brand-blue p-8 mt-10 text-center" data-id="element-510">
            <div className="flex items-center justify-center gap-2 mb-3" data-id="element-511">
              <MapPinIcon className="w-6 h-6 text-white" data-id="element-512" />
              <span className="text-sm font-bold tracking-widest uppercase text-white/70" data-id="element-513">
                Session Location
              </span>
            </div>
            <h4 className="text-xl md:text-2xl font-display font-bold text-white mb-4" data-id="element-514">
              Springwood Leisure Centre, Oakwood, Derby
            </h4>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2" data-id="element-515">
              <div className="flex items-center gap-2 text-white/90 font-medium" data-id="element-516">
                <CalendarDaysIcon className="w-5 h-5" data-id="element-517" />
                <span data-id="element-518">Thursdays</span>
              </div>
              <div className="flex items-center gap-2 text-white/90 font-medium" data-id="element-519">
                <ClockIcon className="w-5 h-5" data-id="element-520" />
                <span data-id="element-521">10 - 11am</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pay and Play */}
      <section className="py-16 md:py-20 bg-white border-t border-gray-100" data-id="element-522">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-523">
          <div className="text-center mb-12" data-id="element-524">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-red uppercase mb-4" data-id="element-525">
              PAY AND PLAY BADMINTON
            </h2>
            <h3 className="text-xl md:text-2xl font-bold text-brand-blue uppercase" data-id="element-526">
              Adults Weekly Social Sessions
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-527">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-528">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-529">
                <ZapIcon className="w-7 h-7" data-id="element-530" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-531">
                Just Play!
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-532">
                For players of all abilities that just want to PLAY! The session
                is an opportunity for players to come together and enjoy a fun
                and social game of Badminton.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-533">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-534">
                <UnlockIcon className="w-7 h-7" data-id="element-535" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-536">
                No Commitment Needed
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-537">
                A great way to get active, meet new people and have fun without
                the commitment of joining a club or finding your own court. Come
                along as an individual or bring a friend.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-538">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-539">
                <AwardIcon className="w-7 h-7" data-id="element-540" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-541">
                Junior Pathway
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-542">
                An additional opportunity for junior players 17+ years that have
                been through the Bright Star Sports weekly classes and are
                looking to continue game play to improve tactical knowledge and
                build confidence.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-543">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-544">
                <ExternalLinkIcon className="w-7 h-7" data-id="element-545" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-546">
                Book Your Session
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-547">
                Book your first session here:{' '}
                <a href="https://www.brightstarsports.com" className="text-brand-blue hover:underline font-medium" data-id="element-548">
                  www.brightstarsports.com
                </a>
                <br data-id="element-549" />
                <span className="font-bold text-brand-blue mt-2 block" data-id="element-550">
                  We can't wait to welcome you to our classes!
                </span>
              </p>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5,
          duration: 0.5
        }} className="rounded-2xl bg-brand-blue p-8 mt-10 text-center" data-id="element-551">
            <div className="flex items-center justify-center gap-2 mb-3" data-id="element-552">
              <MapPinIcon className="w-6 h-6 text-white" data-id="element-553" />
              <span className="text-sm font-bold tracking-widest uppercase text-white/70" data-id="element-554">
                Session Location
              </span>
            </div>
            <h4 className="text-xl md:text-2xl font-display font-bold text-white mb-4" data-id="element-555">
              Lees Brook Academy Chaddesden, Derby, DE21 4QX
            </h4>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2" data-id="element-556">
              <div className="flex items-center gap-2 text-white/90 font-medium" data-id="element-557">
                <CalendarDaysIcon className="w-5 h-5" data-id="element-558" />
                <span data-id="element-559">Mondays</span>
              </div>
              <div className="flex items-center gap-2 text-white/90 font-medium" data-id="element-560">
                <ClockIcon className="w-5 h-5" data-id="element-561" />
                <span data-id="element-562">7.30 - 9pm</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Programme Focus */}

      {/* Olympic Values */}
      <ValuesSection ctaLabel="Book a Session" ctaHref="/book" data-id="element-563" />
    </main>;
}